import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../lib/firebase';

/**
 * The notebook: experiments under a project, and the notes and documents that
 * belong to each.
 *
 * An experiment is a dated entry — "what I did on the 6th, and why". Its data
 * is the readings captured while it was open (they carry its id), its notes
 * are spoken or typed text, and its attachments are the Word and PowerPoint
 * files someone already wrote about it.
 */

export interface Experiment {
  id: string;
  title: string;
  /** yyyy-mm-dd, the day the work was done. A string so it never shifts with time zones. */
  date: string;
  aim: string;
  createdByUid: string;
  createdByName: string | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface Note {
  id: string;
  text: string;
  source: 'voice' | 'typed' | 'document';
  /** The Word file a note was imported from, kept only as a label. */
  fileName?: string | null;
  authorUid: string;
  authorName: string | null;
  createdAt: Timestamp | null;
  editedAt: Timestamp | null;
  /** True until the server has the note. */
  pending?: boolean;
}

export type AttachmentKind = 'docx' | 'pptx';

export interface Attachment {
  id: string;
  name: string;
  kind: AttachmentKind;
  size: number;
  storagePath: string;
  downloadUrl: string;
  /** Plain text pulled out of the file, for the PDF and for search. */
  text: string;
  uploadedByUid: string;
  uploadedByName: string | null;
  createdAt: Timestamp | null;
}

/** Slide decks run larger than documents, so PowerPoint gets the bigger allowance. */
export const MAX_ATTACHMENT_BYTES: Record<AttachmentKind, number> = {
  docx: 25 * 1024 * 1024,
  pptx: 50 * 1024 * 1024,
};

const experimentsCol = (projectId: string) =>
  collection(db, 'projects', projectId, 'experiments');
const experimentDoc = (projectId: string, id: string) =>
  doc(db, 'projects', projectId, 'experiments', id);
const notesCol = (projectId: string, experimentId: string) =>
  collection(db, 'projects', projectId, 'experiments', experimentId, 'notes');
const attachmentsCol = (projectId: string, experimentId: string) =>
  collection(db, 'projects', projectId, 'experiments', experimentId, 'attachments');

/**
 * The experiment whose page is open, if any.
 *
 * Captures and readings are saved deep inside the capture screens, which know
 * nothing about experiments. Rather than thread an id through every one of
 * them, the two places that write (createCapture, saveReadings) stamp whatever
 * is open here. Set while an experiment page is mounted, cleared when it goes.
 */
let activeExperiment: { projectId: string; experimentId: string } | null = null;

export function setActiveExperiment(projectId: string, experimentId: string): () => void {
  activeExperiment = { projectId, experimentId };
  return () => {
    if (activeExperiment?.experimentId === experimentId) activeExperiment = null;
  };
}

/** `{ experimentId }` to spread into a capture or reading in this project, or `{}`. */
export function experimentStamp(projectId: string): { experimentId?: string } {
  return activeExperiment?.projectId === projectId
    ? { experimentId: activeExperiment.experimentId }
    : {};
}

export function watchExperiments(projectId: string, cb: (e: Experiment[]) => void) {
  const q = query(experimentsCol(projectId), orderBy('date', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Experiment);
      // Same day: the one started last comes first, as it would in a paper book
      // read backwards.
      list.sort(
        (a, b) =>
          b.date.localeCompare(a.date) ||
          (b.createdAt?.toMillis?.() ?? Infinity) - (a.createdAt?.toMillis?.() ?? Infinity),
      );
      cb(list);
    },
    () => cb([]),
  );
}

export function watchExperiment(
  projectId: string,
  id: string,
  cb: (e: Experiment | null) => void,
) {
  return onSnapshot(
    experimentDoc(projectId, id),
    (snap) => cb(snap.exists() ? ({ id: snap.id, ...snap.data() } as Experiment) : null),
    () => cb(null),
  );
}

export async function createExperiment(
  projectId: string,
  fields: { title: string; date: string; aim: string },
  by: { uid: string; name: string | null },
): Promise<string> {
  const ref = await addDoc(experimentsCol(projectId), {
    title: fields.title.trim(),
    date: fields.date,
    aim: fields.aim.trim(),
    createdByUid: by.uid,
    createdByName: by.name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateExperiment(
  projectId: string,
  id: string,
  fields: { title: string; date: string; aim: string },
) {
  await updateDoc(experimentDoc(projectId, id), {
    title: fields.title.trim(),
    date: fields.date,
    aim: fields.aim.trim(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteExperiment(projectId: string, id: string) {
  await deleteDoc(experimentDoc(projectId, id));
}

// ---- notes ----

export function watchNotes(projectId: string, experimentId: string, cb: (n: Note[]) => void) {
  const q = query(notesCol(projectId, experimentId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    // Metadata changes too, so a note stops saying "saving" once confirmed.
    { includeMetadataChanges: true },
    (snap) =>
      cb(
        snap.docs.map(
          // The time is estimated until the server confirms it, so a note shows
          // its time at once; "saving" is only for one not yet on the server.
          (d) =>
            ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }), pending: d.metadata.hasPendingWrites }) as Note,
        ),
      ),
    () => cb([]),
  );
}

export async function addNote(
  projectId: string,
  experimentId: string,
  text: string,
  source: Note['source'],
  by: { uid: string; name: string | null },
  fileName: string | null = null,
) {
  await addDoc(notesCol(projectId, experimentId), {
    text: text.trim(),
    source,
    ...(fileName ? { fileName } : {}),
    authorUid: by.uid,
    authorName: by.name,
    createdAt: serverTimestamp(),
    editedAt: null,
  });
}

export async function editNote(projectId: string, experimentId: string, noteId: string, text: string) {
  await updateDoc(doc(notesCol(projectId, experimentId), noteId), {
    text: text.trim(),
    editedAt: serverTimestamp(),
  });
}

/** The longest note the rules accept, imported or written. */
export const MAX_NOTE_CHARS = 100_000;

/**
 * A Word file brought in as a note: its words become a note like any other,
 * which can then be edited, typed into and added to by voice. The file itself
 * is not kept — attaching is for files that should stay as they are.
 */
export async function importWordNote(
  projectId: string,
  experimentId: string,
  file: File,
  by: { uid: string; name: string | null },
): Promise<void> {
  if (attachmentKind(file.name) !== 'docx') {
    throw new Error('Choose a Word (.docx) file. Older .doc files need saving as .docx first.');
  }
  if (file.size > MAX_ATTACHMENT_BYTES.docx) throw new Error('That file is over 25 MB.');
  const { extractDocumentText } = await import('../lib/docText');
  let text: string;
  try {
    text = await extractDocumentText(file, 'docx');
  } catch {
    throw new Error('Could not read that Word file. Open it in Word, save it again as .docx, and retry.');
  }
  if (!text.trim()) throw new Error('That Word file has no text in it to import.');
  if (text.length > MAX_NOTE_CHARS) {
    throw new Error(
      `That document is too long for one note (${text.length.toLocaleString()} characters, the limit is ${MAX_NOTE_CHARS.toLocaleString()}). Split it into smaller files.`,
    );
  }
  await addNote(projectId, experimentId, text, 'document', by, file.name);
}

export async function deleteNote(projectId: string, experimentId: string, noteId: string) {
  await deleteDoc(doc(notesCol(projectId, experimentId), noteId));
}

// ---- attachments ----

export function attachmentKind(fileName: string): AttachmentKind | null {
  const ext = fileName.toLowerCase().split('.').pop();
  return ext === 'docx' || ext === 'pptx' ? ext : null;
}

const CONTENT_TYPES: Record<AttachmentKind, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

export function watchAttachments(
  projectId: string,
  experimentId: string,
  cb: (a: Attachment[]) => void,
) {
  const q = query(attachmentsCol(projectId, experimentId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Attachment)),
    () => cb([]),
  );
}

/**
 * Upload a Word file to an experiment. PowerPoint belongs in progress updates
 * only; 'pptx' stays in AttachmentKind for decks attached before that.
 *
 * Stored under the uploader's own folder, like photos: Storage rules cannot
 * look up project membership, so the download URL saved on the document is
 * what lets the rest of the project open it.
 */
export async function uploadAttachment(
  projectId: string,
  experimentId: string,
  file: File,
  by: { uid: string; name: string | null },
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const kind = attachmentKind(file.name);
  if (kind === 'pptx') {
    throw new Error('PowerPoint files go in Progress Updates. Only Word (.docx) files can be attached here.');
  }
  if (!kind) throw new Error('Only Word (.docx) files can be attached.');
  if (file.size > MAX_ATTACHMENT_BYTES[kind]) {
    throw new Error(`That file is over ${MAX_ATTACHMENT_BYTES[kind] / 1024 / 1024} MB.`);
  }
  // Pulled out before uploading, so a file the reader cannot open is caught
  // while the person is still here — and so the PDF never waits on it.
  const { extractDocumentText } = await import('../lib/docText');
  const text = await extractDocumentText(file, kind).catch(() => '');

  const id = crypto.randomUUID();
  const storagePath = `users/${by.uid}/attachments/${id}.${kind}`;
  const fileRef = ref(storage, storagePath);
  const task = uploadBytesResumable(fileRef, file, {
    contentType: CONTENT_TYPES[kind],
    contentDisposition: `attachment; filename="${file.name.replace(/"/g, '')}"`,
  });
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      (e) => reject(e),
      () => resolve(),
    );
  });
  const downloadUrl = await getDownloadURL(fileRef);

  const record = {
    name: file.name,
    kind,
    size: file.size,
    storagePath,
    downloadUrl,
    text,
    uploadedByUid: by.uid,
    uploadedByName: by.name,
    createdAt: serverTimestamp(),
  };
  await addDoc(attachmentsCol(projectId, experimentId), record);
}

export async function deleteAttachment(projectId: string, experimentId: string, a: Attachment) {
  await deleteDoc(doc(attachmentsCol(projectId, experimentId), a.id));
  // Only the uploader can remove the file itself; for anyone else the record
  // goes and the orphaned file is harmless.
  await deleteObject(ref(storage, a.storagePath)).catch(() => {});
}

// ---- spoken notes ----

const callTranscribeNote = httpsCallable<
  { projectId: string; audio: string; mimeType: string; seconds: number },
  { ok: true; text: string } | { ok: false; error: string }
>(functions, 'transcribeNote', { timeout: 60_000 });

/** Send a hold-to-talk clip to be transcribed. The audio is not kept anywhere. */
export async function transcribeNote(
  projectId: string,
  rec: { blob: Blob; mimeType: string; seconds: number },
): Promise<string> {
  const audio = await blobToBase64(rec.blob);
  const res = await callTranscribeNote({ projectId, audio, mimeType: rec.mimeType, seconds: rec.seconds });
  if (!res.data.ok) throw new Error(res.data.error);
  return res.data.text;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** The words the transcriber should expect in this project. Owner only, by the rules. */
export async function setProjectVocabulary(projectId: string, words: string[]) {
  await updateDoc(doc(db, 'projects', projectId), {
    vocabulary: [...new Set(words.map((w) => w.trim()).filter(Boolean))].slice(0, 80),
  });
}
